import { useState } from 'react';

import { FormField, FormSelect, FormTextarea } from '@/components/form';

const MAX_TERMS = 100;

interface Props {
  advanced: Record<string, unknown>;
  onChange: (
    key: 'transcription_vocabulary' | 'second_ear',
    value: string[] | string | undefined,
  ) => void;
}

const savedTerms = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((term): term is string => typeof term === 'string') : [];

/** One term per line, trimmed, blanks and duplicates dropped. */
const parseTerms = (text: string): string[] => [
  ...new Set(
    text
      .split('\n')
      .map((term) => term.trim())
      .filter(Boolean),
  ),
];

/** Caller speech recognition settings, stored in the configuration's advanced block. */
export const SpeechHintsFields = ({ advanced, onChange }: Props) => {
  const saved = savedTerms(advanced.transcription_vocabulary);
  const savedKey = saved.join('\n');
  // Local draft so a new line being typed is not trimmed away on every keystroke.
  const [draft, setDraft] = useState(savedKey);
  const [syncedKey, setSyncedKey] = useState(savedKey);
  // Saved hints changed elsewhere (discard, version restore, JSON editor): show them.
  if (savedKey !== syncedKey) {
    setSyncedKey(savedKey);
    if (parseTerms(draft).slice(0, MAX_TERMS).join('\n') !== savedKey) setDraft(savedKey);
  }
  const count = parseTerms(draft).length;
  const secondEar = typeof advanced.second_ear === 'string' ? advanced.second_ear : '';

  return (
    <>
      <FormField
        label="Speech recognition hints"
        htmlFor="advanced_transcription_vocabulary"
        description={
          count > MAX_TERMS
            ? `${count} terms — only the first ${MAX_TERMS} are used.`
            : `Words callers say that the agent often mis-hears, one per line (${count}/${MAX_TERMS}).`
        }
      >
        <FormTextarea
          id="advanced_transcription_vocabulary"
          rows={4}
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            const terms = parseTerms(e.target.value).slice(0, MAX_TERMS);
            onChange('transcription_vocabulary', terms.length > 0 ? terms : undefined);
          }}
        />
      </FormField>
      <FormField
        label="Second ear (phone calls)"
        htmlFor="advanced_second_ear"
        description="A second speech model listens to the caller. Log only = saved to the transcript. Assist = also passes spelled codes to the agent."
      >
        <FormSelect
          id="advanced_second_ear"
          value={secondEar}
          onChange={(e) => onChange('second_ear', e.target.value || undefined)}
        >
          <option value="">Off</option>
          <option value="log">Log only</option>
          <option value="assist">Assist the agent</option>
        </FormSelect>
      </FormField>
    </>
  );
};
